// Helper functions for ad-blocking calculations and statistics.
// These are shared between nested and direct modes.

import { authorizedFetch } from '../../../utils';
import * as itemTypes from '../../../items/item-types';

// Config types for determining which API to use
export const AD_BLOCKING_CONFIG_TYPES = {
  TRAFFIC_PARTNER: 'trafficPartner',
  EMBEDDED_FLOW_CONFIG: 'embeddedFlowConfig',
  OFFER_SYNDICATION_CONFIG: 'offerSyndicationConfig'
};

export const normalizeStatus = status => (status || 'pending').toLowerCase();

// Capitalize status for display (e.g., 'blocked' -> 'Blocked')
export const capitalizeStatus = status =>
  status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : 'Pending';

// Determine parent status from item data
export const getParentStatus = item => {
  const parentStatus = normalizeStatus(item.status);
  if (parentStatus === 'blocked' || parentStatus === 'allowed') {
    return parentStatus;
  }
  return 'pending';
};

// Calculate summary stats for verticals/subverticals
export const calculateSummaryStats = data =>
  data.reduce(
    (acc, item) => {
      const parentStatus = getParentStatus(item);

      if (item.subItems && item.subItems.length > 0) {
        item.subItems.forEach(subItem => {
          if (parentStatus === 'blocked') {
            acc.blocked = (acc.blocked || 0) + 1;
          } else {
            const status = normalizeStatus(subItem.status);
            acc[status] = (acc[status] || 0) + 1;
          }
        });
      }
      return acc;
    },
    { blocked: 0, allowed: 0, pending: 0 }
  );

// Filter data by search term
export const filterBySearch = (data, searchValue) => {
  if (!searchValue) return data;
  const lowerSearch = searchValue.toLowerCase();
  return data.filter(item => {
    const nameMatch = item.name.toLowerCase().includes(lowerSearch);
    const subItemMatch = item.subItems?.some(subItem =>
      subItem.name.toLowerCase().includes(lowerSearch)
    );
    return nameMatch || subItemMatch;
  });
};

// Check if a campaign's vertical or sub-vertical is blocked
export const getVerticalBlockingStatus = (campaign, verticalsData) => {
  if (!campaign.verticalId || !verticalsData) return null;

  const vertical = verticalsData.find(v => v.id === campaign.verticalId);
  if (!vertical) return null;

  const verticalStatus = normalizeStatus(vertical.status);
  if (verticalStatus === 'blocked') {
    return {
      isBlocked: true,
      type: 'Vertical',
      isPartnerLevel: !vertical.isAdUnitLevel
    };
  }

  if (campaign.subVerticalId) {
    const subVertical = vertical.subItems?.find(
      sv => sv.id === campaign.subVerticalId
    );
    if (subVertical) {
      const subVerticalStatus = normalizeStatus(subVertical.status);
      if (subVerticalStatus === 'blocked') {
        return {
          isBlocked: true,
          type: 'Sub Vertical',
          isPartnerLevel: !subVertical.isAdUnitLevel
        };
      }
    }
  }

  return { isBlocked: false, type: null, isPartnerLevel: false };
};

// Check if all campaigns in an advertiser are blocked by vertical/sub-vertical
export const areAllCampaignsBlockedByVertical = (advertiser, verticalsData) => {
  if (!advertiser.subItems || advertiser.subItems.length === 0) {
    return false;
  }

  return advertiser.subItems.every(campaign => {
    const verticalBlocking = getVerticalBlockingStatus(campaign, verticalsData);
    return verticalBlocking?.isBlocked;
  });
};

// Calculate summary stats for advertisers, accounting for vertical/sub-vertical blocking
export const calculateAdvertiserSummaryStats = (
  advertisersData,
  verticalsData
) => {
  const stats = { blocked: 0, allowed: 0, pending: 0 };

  advertisersData.forEach(advertiser => {
    if (!advertiser.subItems || advertiser.subItems.length === 0) {
      return;
    }

    const parentStatus = getParentStatus(advertiser);

    advertiser.subItems.forEach(campaign => {
      if (parentStatus === 'blocked') {
        stats.blocked++;
        return;
      }

      const campaignStatus = normalizeStatus(campaign.status);

      const verticalBlocking = getVerticalBlockingStatus(
        campaign,
        verticalsData
      );
      if (verticalBlocking?.isBlocked) {
        stats.blocked++;
        return;
      }

      stats[campaignStatus]++;
    });
  });

  return stats;
};

// Strip isAdUnitLevel and wasBlockedBeforeParent properties from ad-blocking items before sending to backend.
// These properties are only used for UI manipulation and should not be persisted.
export const stripIsAdUnitLevel = items =>
  items?.map(({ isAdUnitLevel, subItems, ...rest }) => ({
    ...rest,
    subItems: subItems?.map(
      ({ isAdUnitLevel: _, wasBlockedBeforeParent: __, ...subRest }) => subRest
    )
  }));

// Project updates to the ID and status fields consumed by the save endpoint
// Display metadata remains client-side and is excluded from the PUT body
const toSubPartnerBlockSaveItems = items =>
  items?.map(item => ({
    id: item.id,
    status: item.status,
    ...(item.subItems?.length > 0 && {
      subItems: item.subItems.map(subItem => ({
        id: subItem.id,
        status: subItem.status
      }))
    })
  })) || [];

// Merge sub-items (sub-verticals or campaigns).
// Only "Blocked" ad-unit sub-items override partner-level.
const mergeSubItems = (partnerSubItems = [], adUnitSubItems = []) => {
  const adUnitSubMap = new Map();

  adUnitSubItems.forEach(item => {
    if (item.status === 'Blocked') {
      adUnitSubMap.set(item.id, { ...item, isAdUnitLevel: true });
    }
  });

  const merged = partnerSubItems.map(partnerSubItem => {
    const adUnitOverride = adUnitSubMap.get(partnerSubItem.id);

    if (adUnitOverride) {
      adUnitSubMap.delete(partnerSubItem.id);
      return { ...partnerSubItem, ...adUnitOverride };
    }

    return { ...partnerSubItem, isAdUnitLevel: false };
  });

  adUnitSubMap.forEach(item => {
    merged.push(item);
  });

  return merged;
};

// Merge partner-level and ad-unit level data for display.
// Ad-unit level "Blocked" entries override partner-level.
// Ad-unit level "Allowed" entries signal a revert to partner state.
export const mergeAdBlockingData = (partnerData, adUnitData) => {
  const adUnitMap = new Map();
  adUnitData.forEach(item => {
    const hasBlockedSubItems = item.subItems?.some(
      sub => sub.isAdUnitLevel && sub.status === 'Blocked'
    );

    if (item.status === 'Blocked' || hasBlockedSubItems) {
      adUnitMap.set(item.id, {
        ...item,
        isAdUnitLevel: item.status === 'Blocked'
      });
    }
  });

  // Merge partner data with ad-unit overrides
  const merged = partnerData.map(partnerItem => {
    const adUnitOverride = adUnitMap.get(partnerItem.id);

    if (adUnitOverride) {
      adUnitMap.delete(partnerItem.id);

      if (adUnitOverride.isAdUnitLevel) {
        return {
          ...partnerItem,
          ...adUnitOverride,
          subItems: mergeSubItems(
            partnerItem.subItems,
            adUnitOverride.subItems || []
          )
        };
      }

      return {
        ...partnerItem,
        isAdUnitLevel: false,
        subItems: mergeSubItems(
          partnerItem.subItems,
          adUnitOverride.subItems || []
        )
      };
    }

    return { ...partnerItem, isAdUnitLevel: false };
  });

  // Add ad-unit items that don't exist in partner data
  adUnitMap.forEach(item => {
    merged.push(item);
  });

  return merged;
};

// ============================================================================
// Ad Blocking API Functions
// ============================================================================

// Helper to validate API response
const validateResponse = (response, operation) => {
  if (!response) {
    throw new Error(`Failed to ${operation}: No response received`);
  }
  return response;
};

// Fetches ad blocking configuration for a traffic partner
export async function fetchTrafficPartnerAdBlocking(trafficPartnerId) {
  const response = await authorizedFetch(
    `/api/${itemTypes.TRAFFIC_PARTNER}/${trafficPartnerId}/AdBlocking`,
    'GET'
  );
  return validateResponse(response, 'fetch traffic partner ad blocking');
}

// Saves ad blocking configuration for a traffic partner
export async function saveTrafficPartnerAdBlocking(
  trafficPartnerId,
  adBlockingData
) {
  const response = await authorizedFetch(
    `/api/${itemTypes.TRAFFIC_PARTNER}/${trafficPartnerId}/AdBlocking`,
    'PUT',
    {
      expectedVersion: adBlockingData.expectedVersion,
      verticals: stripIsAdUnitLevel(adBlockingData.verticals) || [],
      advertisers: stripIsAdUnitLevel(adBlockingData.advertisers) || []
    }
  );
  return validateResponse(response, 'save traffic partner ad blocking');
}

// Fetches Sub Partner Ad Blocking configuration
export async function fetchSubPartnerAdBlocking(trafficPartnerId) {
  const response = await authorizedFetch(
    `/api/${itemTypes.TRAFFIC_PARTNER}/${trafficPartnerId}/SubPartnerAdBlocking`,
    'GET'
  );
  return validateResponse(response, 'fetch sub partner ad blocking');
}

// Saves Sub Partner Ad Blocking configuration
export async function saveSubPartnerAdBlocking(
  trafficPartnerId,
  subPartnerAdBlockingData
) {
  const response = await authorizedFetch(
    `/api/${itemTypes.TRAFFIC_PARTNER}/${trafficPartnerId}/SubPartnerAdBlocking`,
    'PUT',
    {
      expectedVersion: subPartnerAdBlockingData.expectedVersion,
      subPartnerAdBlockingItems:
        subPartnerAdBlockingData.subPartnerBlocks?.map(block => ({
          subPartnerId: block.subPartnerId,
          subPartnerName: block.subPartnerName,
          verticals: toSubPartnerBlockSaveItems(block.verticals),
          advertisers: toSubPartnerBlockSaveItems(block.advertisers)
        })) || []
    }
  );
  return validateResponse(response, 'save sub partner ad blocking');
}

// Fetches ad blocking configuration for an embedded flow config (ad unit config)
export async function fetchEmbeddedFlowConfigAdBlocking(
  trafficPartnerId,
  embeddedFlowConfigId
) {
  const response = await authorizedFetch(
    `/api/${itemTypes.TRAFFIC_PARTNER}/${trafficPartnerId}/embeddedflowconfig/${embeddedFlowConfigId}/AdBlocking`,
    'GET'
  );
  return validateResponse(response, 'fetch embedded flow config ad blocking');
}

// Saves ad blocking configuration for an embedded flow config (ad unit config)
export async function saveEmbeddedFlowConfigAdBlocking(
  trafficPartnerId,
  embeddedFlowConfigId,
  adBlockingData
) {
  const response = await authorizedFetch(
    `/api/${itemTypes.TRAFFIC_PARTNER}/${trafficPartnerId}/embeddedflowconfig/${embeddedFlowConfigId}/AdBlocking`,
    'PUT',
    {
      expectedVersion: adBlockingData.expectedVersion,
      verticals: stripIsAdUnitLevel(adBlockingData.verticals) || [],
      advertisers: stripIsAdUnitLevel(adBlockingData.advertisers) || [],
      trafficPartnerAdBlocking: adBlockingData.trafficPartnerAdBlocking || null
    }
  );
  return validateResponse(response, 'save embedded flow config ad blocking');
}

// Fetches ad blocking configuration for an offer syndication config
export async function fetchOfferSyndicationConfigAdBlocking(
  trafficPartnerId,
  offerSyndicationConfigId
) {
  const response = await authorizedFetch(
    `/api/${itemTypes.TRAFFIC_PARTNER}/${trafficPartnerId}/offersyndicationconfig/${offerSyndicationConfigId}/AdBlocking`,
    'GET'
  );
  return validateResponse(
    response,
    'fetch offer syndication config ad blocking'
  );
}

// Saves ad blocking configuration for an offer syndication config
export async function saveOfferSyndicationConfigAdBlocking(
  trafficPartnerId,
  offerSyndicationConfigId,
  adBlockingData
) {
  const response = await authorizedFetch(
    `/api/${itemTypes.TRAFFIC_PARTNER}/${trafficPartnerId}/offersyndicationconfig/${offerSyndicationConfigId}/AdBlocking`,
    'PUT',
    {
      expectedVersion: adBlockingData.expectedVersion,
      verticals: stripIsAdUnitLevel(adBlockingData.verticals) || [],
      advertisers: stripIsAdUnitLevel(adBlockingData.advertisers) || [],
      trafficPartnerAdBlocking: adBlockingData.trafficPartnerAdBlocking || null
    }
  );
  return validateResponse(
    response,
    'save offer syndication config ad blocking'
  );
}
